import { differenceInMinutes, safeParseISO } from 'shared/util/datetime-fns';
import { formatDistance, formatDuration } from 'shared/util/formatting';
import type { EnrichedFlight, Flight, FlightsResult, HotelOffer, HotelResponse, LegConfig, LegWithData, LegWithRows } from '@/interfaces';
import displayValues from '@/resources/displayValues.json';

const enrichFlights = (flights: Flight[]): EnrichedFlight[] =>
    flights.map((flight, index, allFlights) => {
        const parsedDepartureTime = safeParseISO(flight.departureTime);
        const parsedArrivalTime = safeParseISO(flight.arrivalTime);
        const prevArrival = index > 0 ? safeParseISO(allFlights[index - 1].arrivalTime) : undefined;

        const stopoverMinutes =
            index > 0 && prevArrival && parsedDepartureTime ? differenceInMinutes(prevArrival, parsedDepartureTime) : undefined;

        return {
            flight,
            index,
            parsedDepartureTime,
            parsedArrivalTime,
            stopoverMinutes,
        };
    });

const buildLegsWithRows = (
    legs: LegConfig[],
    outboundFlightsWithComputed: EnrichedFlight[],
    inboundFlightsWithComputed: EnrichedFlight[],
    hasFlightDetails: boolean,
): LegWithRows[] => {
    const legsWithData: LegWithData[] = legs.map((leg) => {
        const summaryDepartureParsed = safeParseISO(leg.summaryDepartureISO);
        const summaryArrivalParsed = safeParseISO(leg.summaryArrivalISO);

        const detailed = leg.key === 'outbound' ? outboundFlightsWithComputed : inboundFlightsWithComputed;
        const hasDetailed = hasFlightDetails && detailed.length > 0;

        let headingDate: Date;

        if (hasDetailed) {
            const firstDeparture = detailed[0]?.parsedDepartureTime;
            if (!firstDeparture) {
                throw new Error('Expected parsedDepartureTime for first detailed flight segment');
            }
            headingDate = firstDeparture;
        } else {
            if (!summaryDepartureParsed) {
                throw new Error('Expected summaryDepartureParsed for leg');
            }
            headingDate = summaryDepartureParsed;
        }

        return {
            ...leg,
            detailed,
            summaryDepartureParsed,
            summaryArrivalParsed,
            headingDate,
            hasDetailed,
        };
    });

    return legsWithData.map((leg) => {
        const { origin, destination, summaryDuration, summaryDepartureParsed, summaryArrivalParsed, hasDetailed, detailed, stopovers } =
            leg;

        const baseRows: EnrichedFlight[] = hasDetailed
            ? detailed
            : summaryDepartureParsed
              ? [
                    {
                        flight: {
                            originAirport: origin,
                            destinationAirport: destination,
                            flightDuration: summaryDuration ?? undefined,
                            airlineCode: undefined,
                            flightNo: undefined,
                            airline: undefined,
                            departureTime: '',
                            arrivalTime: '',
                        } as Flight,
                        index: 0,
                        parsedDepartureTime: summaryDepartureParsed,
                        parsedArrivalTime: summaryArrivalParsed,
                        stopoverMinutes: undefined,
                    },
                ]
              : [];

        const rowsWithStopoverData: EnrichedFlight[] = baseRows.map((row) => {
            const { index, stopoverMinutes, flight } = row;

            const showDetailedStopoverAlert = hasDetailed && index > 0 && stopoverMinutes != null && stopoverMinutes > 0;
            const showSummaryStopoverAlert = !hasDetailed && index === 0 && stopovers != null && stopovers > 0;
            const showStopoverAlert = showDetailedStopoverAlert || showSummaryStopoverAlert;

            let stopoverText: string | undefined;

            if (showStopoverAlert) {
                if (hasDetailed && stopoverMinutes != null) {
                    stopoverText = `Mellomlanding i ${flight.originAirport} (${formatDuration(stopoverMinutes)})`;
                } else if (!hasDetailed && stopovers != null) {
                    stopoverText = stopovers > 1 ? `${stopovers} mellomlandinger` : `${stopovers} mellomlanding`;
                }
            }

            return {
                ...row,
                showStopoverAlert,
                stopoverText,
            };
        });

        return {
            ...leg,
            rows: rowsWithStopoverData,
        };
    });
};

export const buildFlightsResult = (offer: HotelOffer): FlightsResult => {
    const {
        originAirport,
        destinationAirport,
        departureDate,
        departureDateArrival,
        departureStopovers,
        departureTravelDuration,
        returnDate,
        returnDateArrival,
        returnStopovers,
        returnTravelDuration,
        flightDetails,
    } = offer;

    const hasFlightDetails = Boolean(flightDetails);
    const outboundFlights: Flight[] = flightDetails?.outbound ?? [];
    const inboundFlights: Flight[] = flightDetails?.inbound ?? [];

    const outboundFlightsWithComputed = enrichFlights(outboundFlights);
    const inboundFlightsWithComputed = enrichFlights(inboundFlights);

    const legs: LegConfig[] = [
        {
            key: 'outbound',
            isReturn: false,
            summaryDepartureISO: departureDate,
            summaryArrivalISO: departureDateArrival,
            summaryDuration: departureTravelDuration,
            stopovers: departureStopovers,
            origin: originAirport,
            destination: destinationAirport,
            detailedFlights: outboundFlights,
        },
        {
            key: 'inbound',
            isReturn: true,
            summaryDepartureISO: returnDate,
            summaryArrivalISO: returnDateArrival,
            summaryDuration: returnTravelDuration,
            stopovers: returnStopovers,
            origin: destinationAirport,
            destination: originAirport,
            detailedFlights: inboundFlights,
        },
    ];

    const legsWithRows = buildLegsWithRows(legs, outboundFlightsWithComputed, inboundFlightsWithComputed, hasFlightDetails);
    const showUnknownStopoversWarning = !hasFlightDetails && (departureStopovers === undefined || returnStopovers === undefined);

    return {
        legsWithRows,
        showUnknownStopoversWarning,
        hasFlightDetails,
    };
};

export const getPricePerPerson = (pricePerPerson: number, numPax: number, priceTotal?: number) => {
    let calculatedPrice = pricePerPerson;
    if (priceTotal != null) {
        calculatedPrice = Math.round(priceTotal / numPax);
    }
    return calculatedPrice;
};

export const getPriceTotal = (pricePerPerson: number, numPax: number, priceTotal?: number) => priceTotal ?? numPax * pricePerPerson;

export const getNumberOfPax = (numberOfAdults?: number, numberOfChildren?: number) => {
    if (numberOfAdults == null || numberOfChildren == null) {
        return 2;
    } else {
        return numberOfAdults + numberOfChildren;
    }
};

export const formatDistances = (distanceToBeach?: number, distanceToCity?: number): string =>
    [
        distanceToBeach == null ? null : `${formatDistance(distanceToBeach)} fra strand`,
        distanceToCity == null ? null : `${formatDistance(distanceToCity)} fra sentrum`,
    ]
        .filter(Boolean)
        .join(' • ');

const facilitiesMap = displayValues.amenities.facilities as Record<string, string>;
const activitiesMap = displayValues.amenities.activities as Record<string, string>;

export const getAmenitiesList = (hotel: HotelResponse): string[] => {
    const translatedFacilities = (hotel.facilities ?? []).map((key) => facilitiesMap[key]).filter(Boolean);
    const translatedActivities = (hotel.activities ?? []).map((key) => activitiesMap[key]).filter(Boolean);

    return [...translatedFacilities, ...translatedActivities];
};

export const getAmenitiesText = (hotel: HotelResponse): string => getAmenitiesList(hotel).filter(Boolean).join(' • ');

const amenityCategories = [
    'Mat og drikke',
    'Basseng og badefasiliteter',
    'Romfasiliteter',
    'Fellesområder og tjenester',
    'Parkering',
    'Trening og velvære',
    'Familie og barn',
    'Hotelltype',
] as const;

export type AmenityCategory = (typeof amenityCategories)[number];

export type Amenity = { name: string; icon: string };

const orderedAmenities = [
    { name: 'Wi‑Fi', icon: 'Wifi', category: 'Romfasiliteter' },
    { name: 'Klimaanlegg', icon: 'AirCon', category: 'Romfasiliteter' },
    { name: 'Parkering', icon: 'Parking', category: 'Parkering' },
    { name: 'Restaurant', icon: 'Diner', category: 'Mat og drikke' },
    { name: 'Heis', icon: 'Lift', category: 'Fellesområder og tjenester' },
    { name: 'Røykfritt', icon: 'NoSmoking', category: 'Hotelltype' },
    { name: 'Basseng', icon: 'Swimming', category: 'Basseng og badefasiliteter' },
    { name: 'Barnebasseng', icon: 'Swimming', category: 'Basseng og badefasiliteter' },
    { name: 'Barnevennlig', icon: 'SmileyGood', category: 'Hotelltype' },
    { name: 'Balkong/terrasse', icon: 'Check', category: 'Romfasiliteter' },
    { name: 'Resepsjon', icon: 'StoreFront', category: 'Fellesområder og tjenester' },
    { name: 'Hav-/innsjøutsikt (mulig)', icon: 'Check', category: 'Romfasiliteter' },
    { name: 'Spa', icon: 'Sauna', category: 'Trening og velvære' },
    { name: 'Safe', icon: 'LockShield', category: 'Romfasiliteter' },
    { name: 'Bar', icon: 'Drink', category: 'Mat og drikke' },
    { name: 'Kafé', icon: 'Diner', category: 'Mat og drikke' },
    { name: 'Lobby/lounge', icon: 'Sofa', category: 'Fellesområder og tjenester' },
    { name: 'Safe på rom', icon: 'LockShield', category: 'Romfasiliteter' },
    { name: 'TV', icon: 'TV', category: 'Romfasiliteter' },
    { name: 'Tekjøkken', icon: 'Stove', category: 'Romfasiliteter' },
    { name: 'Lekeplass', icon: 'Playhouse', category: 'Familie og barn' },
    { name: 'Barneseng/babyseng', icon: 'Check', category: 'Familie og barn' },
    { name: 'Barneaktiviteter', icon: 'Check', category: 'Familie og barn' },
    { name: 'Bagasjeoppbevaring', icon: 'Bag', category: 'Fellesområder og tjenester' },
    { name: 'Treningsrom', icon: 'Dumbbell', category: 'Trening og velvære' },
    { name: 'Innendørs basseng', icon: 'Swimming', category: 'Basseng og badefasiliteter' },
    { name: 'Oppvarmet basseng', icon: 'Swimming', category: 'Basseng og badefasiliteter' },
    { name: 'All inclusive tilgjengelig', icon: 'Diner', category: 'Mat og drikke' },
    { name: 'Romservice', icon: 'RoomService', category: 'Mat og drikke' },
    { name: 'Vannpark', icon: 'Swimming', category: 'Basseng og badefasiliteter' },
    { name: 'Vannsklier', icon: 'Swimming', category: 'Basseng og badefasiliteter' },
    { name: 'Klimaanlegg i fellesområder', icon: 'AirCon', category: 'Fellesområder og tjenester' },
    { name: 'Wi‑Fi i fellesområder', icon: 'Wifi', category: 'Fellesområder og tjenester' },
    { name: 'Bassengbar', icon: 'Drink', category: 'Mat og drikke' },
    { name: 'Underholdningsprogram', icon: 'Theater', category: 'Fellesområder og tjenester' },
    { name: 'Spillrom', icon: 'GeometricShapes', category: 'Fellesområder og tjenester' },
    { name: 'Nattklubb', icon: 'Drink', category: 'Mat og drikke' },
    { name: 'Voksenhotell (18+)', icon: 'User', category: 'Hotelltype' },
    { name: 'Privat basseng', icon: 'Swimming', category: 'Basseng og badefasiliteter' },
    { name: 'Vaskeservice', icon: 'Vacuum', category: 'Fellesområder og tjenester' },
    { name: 'Bæring av bagasje', icon: 'Bag', category: 'Fellesområder og tjenester' },
    { name: 'Safe i resepsjon', icon: 'LockShield', category: 'Fellesområder og tjenester' },
    { name: 'Miljøvennlig hotell', icon: 'Leaf', category: 'Hotelltype' },
    { name: 'Butikk', icon: 'StoreFront', category: 'Fellesområder og tjenester' },
    { name: 'Minimarked', icon: 'ShoppingCart', category: 'Fellesområder og tjenester' },
    { name: 'Minikiosk', icon: 'StoreFront', category: 'Fellesområder og tjenester' },
    { name: 'Takterrasse', icon: 'Check', category: 'Fellesområder og tjenester' },
    { name: 'Telefon', icon: 'Phone', category: 'Romfasiliteter' },
    { name: 'Basseng (sesong)', icon: 'Swimming', category: 'Basseng og badefasiliteter' },
    { name: 'Rengjøring (dager per uke)', icon: 'Vacuum', category: 'Fellesområder og tjenester' },
    { name: 'Klimaanlegg (mot betaling)', icon: 'AirCon', category: 'Romfasiliteter' },
    { name: 'Wi‑Fi på rom', icon: 'Wifi', category: 'Romfasiliteter' },
    { name: 'Klimaanlegg på rom', icon: 'AirCon', category: 'Romfasiliteter' },
    { name: 'Parkering/garasje (kan være mot betaling)', icon: 'Parking', category: 'Parkering' },
    { name: 'Parkering (utendørs)', icon: 'Parking', category: 'Parkering' },
    { name: 'Utendørs basseng', icon: 'Swimming', category: 'Basseng og badefasiliteter' },
    { name: 'Sentrumsnært', icon: 'PinMarker', category: 'Hotelltype' },
    { name: 'Strand i nærheten', icon: 'Beach', category: 'Basseng og badefasiliteter' },
] as const satisfies (Amenity & { category: AmenityCategory })[];

export const groupAmenitiesByCategory = (amenities: string[]): { category: AmenityCategory; amenities: Amenity[] }[] => {
    const amenitySet = new Set(amenities);
    const matched = orderedAmenities.filter((a) => amenitySet.has(a.name));
    const grouped = new Map<AmenityCategory, Amenity[]>();
    for (const amenity of matched) {
        const list = grouped.get(amenity.category) ?? [];
        list.push({ name: amenity.name, icon: amenity.icon });
        grouped.set(amenity.category, list);
    }
    return amenityCategories.filter((c) => grouped.has(c)).map((category) => ({ category, amenities: grouped.get(category) ?? [] }));
};

export const getTopAmenities = (amenities: string[], limit = 8): Amenity[] => {
    const amenitySet = new Set(amenities);
    return orderedAmenities
        .filter((a) => amenitySet.has(a.name))
        .slice(0, limit)
        .map((a) => ({ name: a.name, icon: a.icon }));
};

export const parseMarkdownSections = (markdown: string) =>
    markdown
        .split(/^## /m)
        .filter(Boolean)
        .map((section) => {
            const [title, ...rest] = section.split('\n');
            return { title: title.trim(), body: rest.join('\n').trim() };
        });
